import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@tabbio/tokens';
import { useToast } from '../../hooks/useToast';
import {
  useCreateShortlist,
  useSetShortlistCandidates,
  useShortlistsList,
} from '../../hooks/useShortlistsApi';
import { isLibrarySetupRequiredError } from '../../lib/library-api-error';
import { WebDialog } from '../common/WebDialog.web';
import { SkeletonLine, SkeletonPill } from '../common/Skeleton.web';

export interface ShortlistAddCandidate {
  id: string;
  name: string;
}

export interface LibraryShortlistAddSheetProps {
  visible: boolean;
  candidate: ShortlistAddCandidate | null;
  onClose: () => void;
}

export function LibraryShortlistAddSheet({
  visible,
  candidate,
  onClose,
}: LibraryShortlistAddSheetProps) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const shortlistsQuery = useShortlistsList({ limit: 100 }, visible);
  const createShortlistMutation = useCreateShortlist();
  const setShortlistCandidatesMutation = useSetShortlistCandidates();
  const [newShortlistName, setNewShortlistName] = useState('');
  const [newShortlistDescription, setNewShortlistDescription] = useState('');

  useEffect(() => {
    if (!visible) {
      setNewShortlistName('');
      setNewShortlistDescription('');
    }
  }, [visible]);

  if (!visible) return null;

  const shortlists = shortlistsQuery.data?.shortlists ?? [];
  const isUpdatingShortlists =
    createShortlistMutation.isPending || setShortlistCandidatesMutation.isPending;

  const showSetupBlockedState = () => {
    toast.error(t('library.setup.blockedTitle'), t('library.setup.blockedMessage'));
  };

  const addCandidateToShortlist = async (
    shortlistId: string,
    existingCandidateIds: string[],
  ) => {
    if (!candidate) return;
    await setShortlistCandidatesMutation.mutateAsync({
      shortlistId,
      candidateIds: [...new Set([...existingCandidateIds, candidate.id])],
    });
    onClose();
    toast.success(t('library.shortlists.savedTitle'));
  };

  const onCreateShortlist = async () => {
    const name = newShortlistName.trim();
    if (!name) {
      toast.error(t('shortlists.create.requiredName'));
      return;
    }

    try {
      const shortlist = await createShortlistMutation.mutateAsync({
        name,
        description: newShortlistDescription.trim() || undefined,
      });
      await addCandidateToShortlist(shortlist.id, shortlist.candidateIds);
    } catch (error) {
      if (isLibrarySetupRequiredError(error)) {
        showSetupBlockedState();
        return;
      }
      toast.error(
        t('library.shortlists.errorTitle'),
        error instanceof Error ? error.message : t('errors.generic'),
      );
    }
  };

  return (
    <WebDialog
      title={t('library.actions.selectShortlist')}
      onClose={onClose}
      maxWidth={520}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shortlists.map((sl) => {
            const alreadyAdded = candidate ? sl.candidateIds.includes(candidate.id) : false;
            const disabled = alreadyAdded || isUpdatingShortlists || !candidate;
            return (
              <button
                key={sl.id}
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  void (async () => {
                    try {
                      await addCandidateToShortlist(sl.id, sl.candidateIds);
                    } catch (error) {
                      if (isLibrarySetupRequiredError(error)) {
                        showSetupBlockedState();
                        return;
                      }
                      toast.error(
                        t('library.shortlists.errorTitle'),
                        error instanceof Error ? error.message : t('errors.generic'),
                      );
                    }
                  })();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  minHeight: 56,
                  paddingInline: 14,
                  paddingBlock: 10,
                  borderRadius: 12,
                  border: `1px solid ${alreadyAdded ? 'var(--text-primary)' : 'var(--input-border)'}`,
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  textAlign: 'start',
                  cursor: disabled ? 'default' : 'pointer',
                  opacity: isUpdatingShortlists && !alreadyAdded ? 0.55 : 1,
                  fontFamily: 'inherit',
                  transition: 'background-color 0.15s ease, border-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (disabled) return;
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                    'var(--alpha-08)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                    'rgba(0,0,0,0.04)';
                }}
              >
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {sl.name}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {t('shortlists.meta.candidates', { count: sl.candidateIds.length })}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: alreadyAdded ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  }}
                >
                  {alreadyAdded
                    ? t('library.shortlists.addedState')
                    : t('library.actions.addToShortlist')}
                </span>
              </button>
            );
          })}
          {shortlistsQuery.isLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingBlock: 8 }}>
              {Array.from({ length: 3 }).map((_, idx) => (
                <div
                  key={`shortlist-add-skeleton-${idx}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 10,
                    paddingInline: 12,
                    paddingBlock: 10,
                    borderRadius: 10,
                    border: '1px solid var(--input-border)',
                  }}
                >
                  <SkeletonLine width="58%" height={14} />
                  <SkeletonPill width={64} height={22} />
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            padding: 16,
            borderRadius: 14,
            border: '1px solid var(--input-border)',
            backgroundColor: 'rgba(0,0,0,0.04)',
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }}>
            {t('library.actions.createShortlist')}
          </span>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
              {t('shortlists.create.nameLabel')}
            </span>
            <input
              type="text"
              value={newShortlistName}
              onChange={(e) => setNewShortlistName(e.target.value)}
              placeholder={t('shortlists.create.namePlaceholder')}
              style={{
                minHeight: 44,
                paddingInline: 12,
                paddingBlock: 8,
                borderRadius: 10,
                border: '1px solid var(--input-border)',
                backgroundColor: 'rgba(0,0,0,0.04)',
                color: 'var(--text-primary)',
                fontSize: 15,
                fontFamily: 'inherit',
                outline: 'none',
              }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
              {t('shortlists.create.descriptionLabel')}
            </span>
            <textarea
              value={newShortlistDescription}
              onChange={(e) => setNewShortlistDescription(e.target.value)}
              placeholder={t('shortlists.create.descriptionPlaceholder')}
              rows={3}
              style={{
                minHeight: 84,
                paddingInline: 12,
                paddingBlock: 8,
                borderRadius: 10,
                border: '1px solid var(--input-border)',
                backgroundColor: 'rgba(0,0,0,0.04)',
                color: 'var(--text-primary)',
                fontSize: 15,
                fontFamily: 'inherit',
                outline: 'none',
                resize: 'vertical',
              }}
            />
          </label>

          <button
            type="button"
            onClick={() => void onCreateShortlist()}
            disabled={isUpdatingShortlists || !newShortlistName.trim()}
            style={{
              minHeight: 44,
              borderRadius: 999,
              border: 'none',
              backgroundColor: 'var(--text-primary)',
              color: '#fff',
              fontSize: 15,
              fontWeight: 700,
              fontFamily: 'inherit',
              cursor:
                isUpdatingShortlists || !newShortlistName.trim() ? 'default' : 'pointer',
              opacity: isUpdatingShortlists || !newShortlistName.trim() ? 0.55 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            {t('library.actions.createShortlist')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes shortlistSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </WebDialog>
  );
}
